import { IRefIds } from 'constants/refs';
import { get, post } from 'services/request';
import { BaseModel, IModelOptions } from './BaseModel';
import { IConceptLinkerAiLinkerItem } from 'types/conceptLinkerAiLinker';
import { IModelUpdateRowTypes } from '../types/models';

export default class ConceptLinkerAiLinkerItemsModel extends BaseModel<IConceptLinkerAiLinkerItem, null> {
  jobId?: number;

  constructor(options: IModelOptions & { jobId?: number }) {
    super(options);
    this.jobId = options.jobId;
    this.ref = IRefIds.conceptLinkerValidator;
    this.gridName = 'ConceptLinkerAiLinkerItems';
    this.getGridColumns();
  }

  setJobId(jobId: number) {
    this.jobId = jobId;
    this.gridData = [];
    this.loadingGrid = true;
    this.render();
    this.getGrid();
  }

  async getGrid() {
    if (!this.jobId) return;

    await super.getGrid();
    this.gridData = await get<IConceptLinkerAiLinkerItem[]>(
      `concept-linker-ai-linker/${this.jobId}/items`,
    );
    this.loadingGrid = false;
    this.render();
  }

  async reviewItem(itemId: number) {
    const response = await post<{
      success: boolean;
      message: string;
      item_id: number;
      status: number;
      decision: string;
    }>(`concept-linker-ai-linker/items/${itemId}/review`, {});

    if (response.data) {
      await this.updateGridRow(itemId, 'update');
    }

    return response;
  }

  async refresh() {
    await this.getGrid();
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async loadItemData(itemId: number) {
    return {};
  }

  async getGridRow(itemId: number) {
    return await get<IConceptLinkerAiLinkerItem>(`concept-linker-ai-linker/items/${itemId}`);
  }

  async updateGridRow(itemId: number, type: IModelUpdateRowTypes = 'update') {
    await super.updateGridRow(itemId, type);

    if (type === 'update' && this.gridDataUpdateRow?.data && Array.isArray(this.gridData)) {
      const index = this.gridData.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        this.gridData[index] = this.gridDataUpdateRow.data as IConceptLinkerAiLinkerItem;
        this.gridData = [...this.gridData];
        this.render();
      }
    } else if (type === 'remove' && Array.isArray(this.gridData)) {
      this.gridData = this.gridData.filter((item) => item.id !== itemId);
    }
  }

  async updateItemConcepts(itemId: number, dialects: Record<string, { phrases: any[] }>) {
    const response = await post<any>(`concept-linker-ai-linker/items/${itemId}/update-concepts`, { dialects });

    if (response.data) {
      // Update grid data in place
      if (Array.isArray(this.gridData)) {
        const index = this.gridData.findIndex((item) => item.id === itemId);
        if (index !== -1) {
          this.gridData[index] = response.data;
          this.gridData = [...this.gridData];
          this.render();
        }
      }
    }

    return response;
  }

  async bulkReview() {
    this.loadingGrid = true;
    this.render();
    const response = await post<{
      success: boolean;
      message: string;
      processed: number;
      errors: Array<{ item_id: number; error: string }>;
    }>(`concept-linker-ai-linker/${this.jobId}/bulk-review`, {});

    if (response.data) {
      await this.getGrid();
    }

    this.loadingGrid = false;
    this.render();

    return response;
  }
}
